// Recuperação de "build velho na aba".
//
// O app é servido em chunks com hash no nome (CampanhasPage-A1b2c3.js). Quando
// sobe um deploy, os arquivos antigos deixam de existir. Uma aba que já estava
// aberta continua com o index.html velho e, ao navegar pra uma rota lazy, tenta
// baixar um chunk que sumiu → o import falha, o erro sobe até a raiz e o React
// desmonta tudo: tela branca até o usuário dar um ctrl+shift+R.
//
// Aqui a gente reconhece esse erro específico e recarrega sozinho, uma vez só.

const RELOAD_GUARD_KEY = "north:stale-chunk-reload";
const RELOAD_COOLDOWN_MS = 30_000; // se recarregar e quebrar de novo, não entra em loop
const RELOAD_PARAM = "__reload";

const CHUNK_ERROR_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css|chunkloaderror|loading chunk \d+ failed|build desatualizado/i;

/** O erro é "chunk que não existe mais", e não um bug de verdade da aplicação? */
export const isStaleChunkError = (error: unknown): boolean => {
  if (!error) return false;
  const message =
    typeof error === "string"
      ? error
      : `${(error as { name?: string }).name ?? ""} ${(error as { message?: string }).message ?? ""}`;
  return CHUNK_ERROR_RE.test(message);
};

/**
 * Recarrega buscando o index.html novo. O parâmetro na URL existe só pra furar
 * qualquer cache de HTML no caminho (browser, CDN) — é limpo assim que a
 * página volta. Devolve false quando já recarregou há pouco: aí o problema não
 * era build velho e insistir só ia deixar a aba piscando.
 */
export const reloadForNewBuild = (): boolean => {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
  } catch {
    // sessionStorage bloqueado (modo restrito): segue sem guarda.
  }
  const now = Date.now();
  if (last && now - last < RELOAD_COOLDOWN_MS) return false;

  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // idem
  }

  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, String(now));
  window.location.replace(url.toString());
  return true;
};

/**
 * `lazy()` de um export nomeado, à prova de build velho.
 *
 * Quando o índice em cache é de um build e os chunks são de outro, o import
 * pode até resolver, mas o módulo vem sem o export esperado — e o
 * `m.NomeDaPagina` explode com "Cannot read properties of undefined", que não
 * parece erro de chunk e escapava da recuperação. Aqui a gente reconhece o
 * caso, recarrega e devolve um erro com nome, em vez de um TypeError solto.
 */
export const importarComponente = <M extends object, K extends keyof M>(
  carregar: () => Promise<M>,
  nome: K,
): Promise<{ default: M[K] }> =>
  carregar().then((modulo) => {
    const componente = modulo?.[nome];
    if (!componente) {
      reloadForNewBuild();
      throw new Error(`Build desatualizado: módulo carregado sem o export "${String(nome)}".`);
    }
    return { default: componente };
  });

/**
 * Recarga pedida pelo usuário, no botão. Não passa pela guarda de 30s: se ele
 * clicou, ele quer, e não há laço a temer — laço é o recarregar automático.
 * Limpa a marca para o automático voltar a valer depois.
 */
export const forcarRecarga = () => {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // sessionStorage bloqueado (modo restrito): segue sem guarda.
  }
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, String(Date.now()));
  window.location.replace(url.toString());
};

/** Tira o parâmetro de recarga da barra de endereço, sem recarregar de novo. */
const limpaParamDeRecarga = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(RELOAD_PARAM) && !url.searchParams.has("__build")) return;
  url.searchParams.delete(RELOAD_PARAM);
  url.searchParams.delete("__build");
  window.history.replaceState({}, "", url.toString());
};

/**
 * Liga os ouvintes globais. Fica em main.tsx, fora da árvore do React, pra
 * continuar valendo mesmo quando o app inteiro desmonta por causa do erro.
 */
export const installStaleChunkRecovery = () => {
  if (typeof window === "undefined") return;

  limpaParamDeRecarga();

  // Vite avisa quando o preload de um chunk falha — o caso mais comum.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault(); // sem isso o Vite deixa o erro estourar na tela
    reloadForNewBuild();
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleChunkError(event.reason)) reloadForNewBuild();
  });

  window.addEventListener("error", (event) => {
    if (isStaleChunkError(event.error ?? event.message)) reloadForNewBuild();
  });
};
